import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error('Heslo musí mít alespoň 8 znaků');
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Hesla se neshodují');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, pwd);
      const data = res.data;
      // Log the user in automatically
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      setDone(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chyba – odkaz je možná neplatný nebo vypršel.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border border-orange-100" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-6" data-testid="reset-password-success">
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Hotovo!</h1>
              <p className="text-gray-600">
                Vaše heslo bylo úspěšně změněno. Přesměrováváme vás do vašeho účtu…
              </p>
            </div>
          ) : (
            <>
              <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-orange-600 mb-4">
                <ArrowLeft className="w-4 h-4" /> Zpět na přihlášení
              </Link>
              <h1 className="text-2xl font-bold mb-2">Nastavte si nové heslo</h1>
              <p className="text-gray-600 mb-6 text-sm">
                Zvolte silné heslo – minimálně <strong>8 znaků</strong>.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="rp-pwd">Nové heslo</Label>
                  <div className="relative mt-1">
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="rp-pwd"
                      type={show ? 'text' : 'password'}
                      className="pl-10 pr-10"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      required
                      minLength={8}
                      data-testid="reset-password-new-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="rp-pwd2">Potvrdit heslo</Label>
                  <div className="relative mt-1">
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      id="rp-pwd2"
                      type={show ? 'text' : 'password'}
                      className="pl-10"
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      required
                      minLength={8}
                      data-testid="reset-password-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6"
                  data-testid="reset-password-submit"
                >
                  {loading ? 'Ukládání…' : 'Nastavit nové heslo'}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
